import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { createLedgerFull, fetchLedgerFull, updateLedger } from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { AccountGroupFlat } from '../api/types'
import { c } from '../theme'

interface Props {
  code?: string
  groupCode: string
  /** Every group, so an existing account can be re-filed into one. */
  groups?: AccountGroupFlat[]
  onClose: () => void
  onSaved: () => void
}

export default function LedgerDialog({
  code, groupCode, groups, onClose, onSaved,
}: Props) {
  const { t } = useTranslation()
  const editing = !!code
  const [form, setForm] = useState({
    code: '', name_en: '', name_ar: '', opening_balance: '0.00',
    opening_dr_cr: 'D', maintain_bill_wise: false, credit_days: '',
    tax_number: '', is_active: true, group_code: groupCode,
    cr_number: '', address: '', city: '', phone: '', email: '',
    contact_person: '',
  })
  const [error, setError] = useState<string | null>(null)

  const existing = useQuery({
    enabled: editing,
    queryKey: ['ledger-full', code],
    queryFn: () => fetchLedgerFull(code!),
  })

  useEffect(() => {
    if (existing.data) {
      const d = existing.data
      setForm({
        code: d.code, name_en: d.name_en, name_ar: d.name_ar ?? '',
        opening_balance: d.opening_balance, opening_dr_cr: d.opening_dr_cr,
        maintain_bill_wise: d.maintain_bill_wise,
        credit_days: d.credit_days?.toString() ?? '',
        tax_number: d.tax_number ?? '', is_active: d.is_active,
        group_code: d.group_code,
        cr_number: d.cr_number ?? '', address: d.address ?? '',
        city: d.city ?? '', phone: d.phone ?? '', email: d.email ?? '',
        contact_person: d.contact_person ?? '',
      })
    }
  }, [existing.data])

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name_en: form.name_en,
        name_ar: form.name_ar || undefined,
        opening_balance: form.opening_balance || '0.00',
        opening_dr_cr: form.opening_dr_cr,
        maintain_bill_wise: form.maintain_bill_wise,
        credit_days: form.credit_days ? Number(form.credit_days) : undefined,
        // Empty string, not undefined: the server reads '' as "clear
        // this", and undefined as "not sent". Sending undefined for a
        // field the user just emptied would silently keep the old value.
        tax_number: form.tax_number,
        cr_number: form.cr_number,
        address: form.address,
        city: form.city,
        phone: form.phone,
        email: form.email,
        contact_person: form.contact_person,
      }
      if (editing) {
        return updateLedger(code!, {
          ...body, is_active: form.is_active,
          // Only when it actually changed: sending the current group on
          // every save would make an untouched edit look like a move.
          group_code: form.group_code !== existing.data?.group_code
            ? form.group_code : undefined,
        })
      }
      // Blank code means "allocate one" -- handled server-side under a lock.
      return createLedgerFull({ ...body, group_code: groupCode,
                                code: form.code || undefined })
    },
    onSuccess: () => { onSaved(); onClose() },
    onError: (e) => setError(errorMessage(e)),
  })

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }))

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {editing ? t('chart.editLedger') : t('chart.newLedger')}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}
          onClose={() => setError(null)}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={2}>
            <TextField label={t('entry.code')} value={form.code}
              disabled={editing}
              onChange={(e) => set('code', e.target.value)}
              helperText={editing ? undefined : t('chart.codeAutoHint')}
              sx={{ width: 190 }} />
            {/* Editable while editing: an account created on the fast
                path lands in the control group, and this is where the
                accountant files it under the right one. Restricted to
                the same nature -- the server refuses a move across
                natures, because it would reclassify every entry ever
                posted to the account. */}
            {editing && groups?.length ? (
              <TextField select label={t('chart.group')} sx={{ flex: 1 }}
                value={form.group_code}
                onChange={(e) => set('group_code', e.target.value)}
                helperText={t('chart.moveHint')}>
                {groups
                  .filter((g) => g.nature === existing.data?.nature)
                  .map((g) => (
                    <MenuItem key={g.id} value={g.code}>{g.name_en}</MenuItem>
                  ))}
              </TextField>
            ) : (
              <TextField label={t('chart.group')} value={groupCode} disabled
                sx={{ flex: 1 }} />
            )}
          </Stack>
          <TextField label={t('entry.name')} value={form.name_en} autoFocus
            onChange={(e) => set('name_en', e.target.value)} />
          <TextField label={`${t('entry.name')} (العربية)`} value={form.name_ar}
            onChange={(e) => set('name_ar', e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField label={t('chart.openingBalance')}
              value={form.opening_balance} className="num"
              onChange={(e) => set('opening_balance',
                e.target.value.replace(/[^\d.]/g, ''))} sx={{ flex: 1 }} />
            <TextField select label={t('entry.drcr')} value={form.opening_dr_cr}
              onChange={(e) => set('opening_dr_cr', e.target.value)}
              sx={{ width: 110 }}>
              <MenuItem value="D">Dr</MenuItem>
              <MenuItem value="C">Cr</MenuItem>
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label={t('entry.creditDays')} value={form.credit_days}
              inputMode="numeric"
              onChange={(e) => set('credit_days',
                e.target.value.replace(/\D/g, ''))} sx={{ width: 140 }} />
            <TextField label={t('chart.vatNumber')} value={form.tax_number}
              onChange={(e) => set('tax_number', e.target.value)}
              sx={{ flex: 1 }} />
          </Stack>
          {/* Contact details.
              Shown for any account but meant for parties: a trading
              business's customer record has to carry the CR and VAT
              numbers the customer's own accounts department checks before
              paying, and the address that has to appear on the tax
              invoice. All optional -- a customer added mid-voucher with
              Alt+C has a name and nothing else, and this is where the
              rest gets filled in later. */}
          <Divider textAlign="left" sx={{ pt: 1 }}>
            <Typography variant="caption" sx={{ color: c.inkMuted }}>
              {t('chart.contactSection')}
            </Typography>
          </Divider>
          <Stack direction="row" spacing={2}>
            <TextField label={t('chart.crNumber')} value={form.cr_number}
              onChange={(e) => set('cr_number', e.target.value)}
              sx={{ flex: 1 }} />
            <TextField label={t('chart.contactPerson')}
              value={form.contact_person}
              onChange={(e) => set('contact_person', e.target.value)}
              sx={{ flex: 1 }} />
          </Stack>
          <TextField label={t('chart.address')} value={form.address}
            onChange={(e) => set('address', e.target.value)}
            multiline minRows={2} />
          <Stack direction="row" spacing={2}>
            <TextField label={t('chart.city')} value={form.city}
              onChange={(e) => set('city', e.target.value)}
              sx={{ width: 180 }} />
            <TextField label={t('chart.phone')} value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              sx={{ flex: 1 }} />
          </Stack>
          <TextField label={t('chart.email')} value={form.email} type="email"
            onChange={(e) => set('email', e.target.value)} />

          <FormControlLabel
            control={<Switch checked={form.maintain_bill_wise}
              onChange={(e) => set('maintain_bill_wise', e.target.checked)} />}
            label={t('chart.billWiseLabel')} />
          <Typography variant="caption" color="text.secondary">
            {t('chart.billWiseHint')}
          </Typography>
          {editing && (
            <FormControlLabel
              control={<Switch checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)} />}
              label={t('chart.active')} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!form.name_en.trim() || save.isPending}
          onClick={() => save.mutate()}>
          {save.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
